/**
 * "Open POS" — walk the signed-in SaaS user into the Laravel POS for a business.
 *
 * Exchanges the SaaS session for a short-lived, single-use POS-handoff token
 * (POST /pos/handoff), then navigates the browser to the POS's single-use
 * exchange endpoint (GET <pos-base>/sso/exchange?token=…&return_to=…), which
 * verifies the token, logs the user in, and lands them on returnTo.
 *
 * The POS base URL is per-reseller-domain (resolved in the domain/brand
 * workstream). Until VITE_POS_BASE is configured it can't navigate, so it
 * surfaces a notice instead of a broken redirect.
 */
import { USE_MOCKS, apiPost } from './client';

const POS_BASE_FALLBACK = (import.meta.env.VITE_POS_BASE ?? '').replace(/\/$/, '');

/** Hand the single-use token to the POS exchange endpoint, dropping it from the
 *  URL on landing. Prefers the server-resolved per-business `posBaseUrl`; falls
 *  back to the build-time VITE_POS_BASE. Returns false (with a notice) if neither. */
function navigateToPos(token: string, returnTo: string, posBaseUrl?: string | null): boolean {
  const base = (posBaseUrl ?? '').replace(/\/$/, '') || POS_BASE_FALLBACK;
  if (!base) {
    window.alert('POS base URL is not configured for this business yet (no primary domain).');
    return false;
  }
  window.location.href = `${base}/sso/exchange?token=${encodeURIComponent(token)}&return_to=${encodeURIComponent(returnTo)}`;
  return true;
}

/** Self-service "Open POS" — the signed-in owner/staff walks into their own POS. */
export async function openPos(businessId: string | undefined, returnTo = '/home'): Promise<void> {
  if (!businessId) return;
  if (USE_MOCKS) {
    window.alert('Open POS — in real mode this SSO-hands you into the Laravel POS for this business.');
    return;
  }
  const { token, posBaseUrl } = await apiPost<{ token: string; posBaseUrl?: string | null }>('/pos/handoff', { businessId });
  navigateToPos(token, returnTo, posBaseUrl);
}

/**
 * Admin support "Open POS" — an audited, read-only-flagged handoff that signs the
 * platform admin into a business's POS AS the business owner (the only identity
 * Laravel can resolve into that POS). The server writes a `pos.impersonation.start`
 * audit row; the token carries `ro`+`imp`. Platform-only on the server.
 */
export async function openBusinessPosAsAdmin(businessId: string | undefined, returnTo = '/home'): Promise<void> {
  if (!businessId) return;
  if (USE_MOCKS) {
    window.alert('Open POS (admin) — in real mode this audited, read-only-flagged handoff signs you into the business POS as the owner for support.');
    return;
  }
  const { token, posBaseUrl } = await apiPost<{ token: string; posBaseUrl?: string | null }>(`/admin/businesses/${businessId}/open-pos`, {});
  navigateToPos(token, returnTo, posBaseUrl);
}
